import React from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
    buildPlannerCalendarEvents,
    getPlannerCalendarInitialView,
    getPlannerCalendarRange,
} from "../utils/plannerCalendar";
import "./PlannerCalendarView.css";

export default function PlannerCalendarView({
    effectivePeriod,
    planSectionsData = {},
    planFocusItems = [],
    supportingWeekBreakdown = null,
    supportingWeekPeriodKey = null,
    tasksList = [],
    projectsList = [],
    now = new Date(),
    fromDate = null,
    toDate = null,
    planDate = null,
    isReadOnly = false,
    onPlannerScheduleMove = null,
    onTaskDeadlineMove = null,
    onDateDoubleClick = null,
    onTodayClick = null,
    title = "Calendar",
    description = "Planner items and task deadlines are projected into a calendar view for this period. Double-click a slot to add one.",
    emptyStateMessage = "There are no planner schedule items or task deadlines in this range yet.",
    showInfoBar = true,
    showToolbar = true,
    calendarHeight = "auto",
    wrapperClassName = "mt-5 space-y-4",
    calendarContainerClassName = "",
    action = null,
}) {
    const calendarRef = React.useRef(null);
    const calendarRange = React.useMemo(() => getPlannerCalendarRange({
        effectivePeriod,
        now,
        fromDate,
        toDate,
        planDate,
    }), [effectivePeriod, fromDate, now, planDate, toDate]);

    const calendarEvents = React.useMemo(() => buildPlannerCalendarEvents({
        effectivePeriod,
        planSectionsData,
        planFocusItems,
        supportingWeekBreakdown,
        supportingWeekPeriodKey,
        tasksList,
        projectsList,
        now,
        fromDate,
        toDate,
        planDate,
    }), [effectivePeriod, fromDate, now, planDate, planFocusItems, planSectionsData, projectsList, supportingWeekBreakdown, supportingWeekPeriodKey, tasksList, toDate]);

    const initialView = React.useMemo(
        () => getPlannerCalendarInitialView({ effectivePeriod, range: calendarRange }),
        [calendarRange, effectivePeriod],
    );

    const calendarKey = React.useMemo(
        () => `${effectivePeriod}-${initialView}-${calendarRange.initialDate.getTime()}`,
        [calendarRange.initialDate, effectivePeriod, initialView],
    );

    const handleEventDrop = React.useCallback(async (changeInfo) => {
        const source = changeInfo.event.extendedProps?.source;
        const isEditable = changeInfo.event.extendedProps?.isPlannerEditable;

        if (isReadOnly || !isEditable) {
            changeInfo.revert();
            return;
        }

        try {
            if (source === "plannerSchedule" && onPlannerScheduleMove) {
                await onPlannerScheduleMove(changeInfo);
                return;
            }

            if (source === "taskDeadline" && onTaskDeadlineMove) {
                await onTaskDeadlineMove(changeInfo);
                return;
            }

            changeInfo.revert();
        } catch (error) {
            changeInfo.revert();
        }
    }, [isReadOnly, onPlannerScheduleMove, onTaskDeadlineMove]);

    const handleDateClick = React.useCallback((clickInfo) => {
        if (isReadOnly || clickInfo?.jsEvent?.detail !== 2 || !onDateDoubleClick) return;
        onDateDoubleClick(clickInfo);
    }, [isReadOnly, onDateDoubleClick]);

    const handleTodayButtonClick = React.useCallback(() => {
        const handledByParent = onTodayClick ? onTodayClick() : false;
        if (handledByParent) return;
        calendarRef.current?.getApi()?.today();
    }, [onTodayClick]);

    return (
        <div className={wrapperClassName}>
            {showInfoBar && (
                <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3'>
                    <div>
                        <p className='text-[11px] font-semibold uppercase tracking-widest text-[var(--brand-coral)]'>{title}</p>
                        <p className='mt-1 text-sm text-[var(--text-secondary)]'>
                            {description}
                        </p>
                    </div>
                    <div className='flex items-center gap-3'>
                        {action}
                        <span className='rounded-full border border-[var(--border-strong)] bg-[var(--surface-deep)] px-3 py-1 text-xs text-[var(--text-secondary)]'>
                            {calendarEvents.length} items
                        </span>
                    </div>
                </div>
            )}

            <div className={`planner-fullcalendar overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--surface)] p-3 ${calendarContainerClassName}`.trim()}>
                <FullCalendar
                    ref={calendarRef}
                    key={calendarKey}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView={initialView}
                    initialDate={calendarRange.initialDate}
                    events={calendarEvents}
                    height={calendarHeight}
                    weekends
                    nowIndicator
                    dayMaxEvents
                    editable={!isReadOnly}
                    selectable={false}
                    allDaySlot
                    eventDisplay='block'
                    slotMinTime='06:00:00'
                    slotMaxTime='23:00:00'
                    dateClick={handleDateClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventDrop}
                    customButtons={showToolbar ? {
                        plannerToday: {
                            text: "today",
                            click: handleTodayButtonClick,
                        },
                    } : undefined}
                    headerToolbar={showToolbar ? {
                        left: "prev,next plannerToday",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                    } : false}
                    eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }}
                />
            </div>

            {calendarEvents.length === 0 && (
                <p className='rounded-xl border border-dashed border-[var(--border-strong)] bg-[var(--surface-deep)] px-4 py-3 text-sm text-[var(--text-muted)]'>
                    {emptyStateMessage}
                </p>
            )}
        </div>
    );
}
